import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar, { SidebarDescription } from "../../Common/Sidebar/Sidebar";
import PreCard from "../PreCard/PreCard";
import Modal from "../Modal/Modal";
import "./Category.scss";
import {
  fetchListingsFromCollection,
  SUBGRAPH_URL,
  MARKET_ADDRESS,
  GET_ALL_COLLECTIONS,
} from "../Market/Market";
import PreHero from "../PreHero/PreHero";
import CountryDropdownPanel from "./CountryDropdownPanel";
import { BurgerIcon, ICO_ARROW } from "../../../assets";
import {
  parseSearch,
  buildSearch,
  normalizeCollectionAddress,
} from "./categoryUrlParams";
import { useGraphQL } from "../../../hooks/useGraphQL";
import { MARKET_ABI } from "../../../contracts/contracts";
import useMediaQuery from "../../../hooks/useMediaQuery";
import SEO from "../../../shared/components/SEO";
import {
  organizationSchema,
  buildBreadcrumbSchema,
  buildGraph,
  truncateDescription,
  SITE_URL,
} from "../../../shared/utils/seoSchemas";

const MARKET_V1 = process.env.REACT_APP_MARKETPLACE_ADDRESS_V1?.toLowerCase();
const MARKET_V2 = process.env.REACT_APP_MARKETPLACE_ADDRESS_V2?.toLowerCase();

async function fetchAllMarketListings() {
  const PAGE = 1000;
  let all = [];
  let skip = 0;
  while (true) {
    const q = `{listings(where:{active:true},first:${PAGE},skip:${skip}){market token{collection{id}}}}`;
    const res = await fetch(SUBGRAPH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: q }),
    });
    const json = await res.json();
    const batch = json.data?.listings || [];
    all = all.concat(batch);
    if (batch.length < PAGE) break;
    skip += PAGE;
  }
  return all;
}

const DEFAULT_COUNTRY = {
  name: "Palestine",
  subgraphUrl: SUBGRAPH_URL,
  id: "0xaa1d815dbc771f01d20e2845fc0653592a4cd5cf",
};

const ITEMS_PER_PAGE = 25;
const HERO_FULL_HEIGHT = 280;
const HERO_COLLAPSED_HEIGHT = 100;
const MOBILE_MENU_COLLAPSE_MS = 700;

const CountryTrigger = ({ selectedCountry, groupLabel, isOpen, onToggle }) => {
  const isTablet = useMediaQuery("(max-width: 768px)");

  return (
    <button className="country-trigger" onClick={() => onToggle(!isOpen)}>
      <span className="country-trigger-name">
        {selectedCountry.name}
        {isTablet && ` - ${groupLabel}`}
      </span>
      <div className="icon-container">
        <img
          src={ICO_ARROW}
          alt="arrow"
          style={{
            transform: isOpen ? "rotate(-90deg)" : "none",
            transition: "transform 0.3s ease",
          }}
        />
      </div>
    </button>
  );
};

const Category = ({
  route = "/reproduction/collections",
  defaultTitle = "Historical Reproduction",
  collectionsData = null,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: dataCollections } = useGraphQL(GET_ALL_COLLECTIONS);
  const [allMarketListings, setAllMarketListings] = useState([]);

  useEffect(() => {
    fetchAllMarketListings().then(setAllMarketListings);
  }, []);

  const { v1CollectionIds, v2CollectionIds, v2ListingCounts } = useMemo(() => {
    const chronicles = new Set();
    const heritage = new Set();
    const heritageCounts = new Map();
    for (const l of allMarketListings) {
      const id = l.token?.collection?.id?.toLowerCase();
      if (!id) continue;
      if (l.market?.toLowerCase() === MARKET_V1) {
        chronicles.add(id);
      } else if (l.market?.toLowerCase() === MARKET_V2) {
        heritage.add(id);
        heritageCounts.set(id, (heritageCounts.get(id) || 0) + 1);
      }
    }
    return {
      v1CollectionIds: chronicles,
      v2CollectionIds: heritage,
      v2ListingCounts: heritageCounts,
    };
  }, [allMarketListings]);

  const searchParams = useMemo(
    () => parseSearch(location.search),
    [location.search],
  );
  useEffect(() => {
    if (!searchParams.collection) {
      setSelectedCountry(DEFAULT_COUNTRY);
      syncAndFetch(DEFAULT_COUNTRY.id, DEFAULT_COUNTRY);
    }
  }, []);

  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeCollection, setActiveCollection] = useState({
    ...DEFAULT_COUNTRY,
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [traits, setTraits] = useState([]);
  const [selectedTrait, setSelectedTrait] = useState(null);
  const [heroShrinkProgress, setHeroShrinkProgress] = useState(0);
  const [tabletDescriptionState, setTabletDescriptionState] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileMenuBar, setShowMobileMenuBar] = useState(true);
  const closeTimeoutRef = useRef(null);
  const [collectionImages, setCollectionImages] = useState({});
  const [sidebarCollections, setSidebarCollections] = useState([]);
  const lastFetchRef = useRef({ collection: null, hasV2: false });
  const gateways = [
    "https://funooni-public.mypinata.cloud/ipfs/",
    "https://ipfs.io/ipfs/",
    "https://cloudflare-ipfs.com/ipfs/",
    "https://gateway.pinata.cloud/ipfs/",
  ];

  const safeImage = (uri, gateway) => {
    if (!uri) return "";

    if (uri.startsWith("ipfs://")) {
      const hash = uri.replace("ipfs://", "");
      return `${gateway}${hash}`;
    }

    return uri;
  };
  const fetchIPFSJson = async (uri) => {
    if (!uri) throw new Error("Invalid IPFS URI");

    let lastError = null;

    for (const gateway of gateways) {
      try {
        const url = safeImage(uri, gateway);

        const res = await fetch(url, {
          cache: "force-cache",
        });

        if (!res.ok) {
          throw new Error(`Gateway failed: ${gateway}`);
        }

        return await res.json();
      } catch (err) {
        console.warn(`❌ ${gateway} failed`);
        lastError = err;
      }
    }

    throw lastError;
  };
  const getSafeImage = async (uri) => {
    if (!uri) return "";

    for (const gateway of gateways) {
      const url = safeImage(uri, gateway);

      try {
        const res = await fetch(url, {
          method: "HEAD",
        });

        if (res.ok) {
          return url;
        }
      } catch {}
    }

    return "";
  };

  useEffect(() => {
    const loadCollectionImages = async () => {
      if (!dataCollections?.collections) return;

      const result = {};

      await Promise.all(
        dataCollections.collections.map(async (collection) => {
          try {
            if (!collection.contractURI) return;

            const meta = await fetchIPFSJson(collection.contractURI);

            result[collection.id] = await getSafeImage(meta.image);
          } catch (err) {
            console.error("Metadata error", err);
          }
        }),
      );

      setCollectionImages(result);

      if (selectedCountry?.id && result[selectedCountry.id]) {
        setActiveCollection((prev) => ({
          ...prev,
          image: result[selectedCountry.id],
        }));
      }
    };

    loadCollectionImages();
  }, [dataCollections, selectedCountry]);
  const countryGroups = useMemo(() => {
    const allCols = dataCollections?.collections || [];

    const chroniclesCollections = allCols.filter((col) =>
      v1CollectionIds.has(col.id.toLowerCase()),
    );

    const groups = [];

    if (v2CollectionIds.size > 0) {
      groups.push({
        label: "Cultural Heritage",
        items: [{ ...DEFAULT_COUNTRY, subgraphUrl: SUBGRAPH_URL }],
      });
    }

    if (chroniclesCollections.length > 0) {
      groups.push({
        label: "World Chronicles",
        items: chroniclesCollections.map((col) => ({
          ...col,
          id: col.id.toLowerCase(),
          subgraphUrl: SUBGRAPH_URL,
        })),
      });
    }

    return groups;
  }, [dataCollections, v1CollectionIds, v2CollectionIds]);

  const selectedGroupLabel = useMemo(
    () =>
      countryGroups.find((g) =>
        g.items.some((i) => i.name === selectedCountry.name),
      )?.label || "",
    [selectedCountry],
  );

  const computeYearSizeTraits = (items) => {
    const traitMap = {};
    items.forEach((item) => {
      if (item.year && item.year !== "Unknown") {
        const key = `Year: ${item.year}`;
        if (!traitMap[key]) traitMap[key] = { name: key, count: 0 };
        traitMap[key].count += 1;
      }
      if (item.size && item.size !== "N/A") {
        const key = `Size: ${item.size}`;
        if (!traitMap[key]) traitMap[key] = { name: key, count: 0 };
        traitMap[key].count += 1;
      }
    });
    return Object.values(traitMap).sort((a, b) => a.name.localeCompare(b.name));
  };

  const syncAndFetch = useCallback(
    async (collectionId, country) => {
      if (!collectionId) return;

      setLoading(true);

      try {
        const matched = dataCollections?.collections?.find(
          (col) => col.id.toLowerCase() === collectionId.toLowerCase(),
        );

        const activeCol = matched
          ? {
              ...matched,
              id: matched.id.toLowerCase(),
              image:
                collectionImages[matched.id.toLowerCase()] ||
                matched.image ||
                "",
            }
          : { id: collectionId, name: country?.name || "" };

        setActiveCollection(activeCol);

        const isHeritage =
          collectionId.toLowerCase() === DEFAULT_COUNTRY.id.toLowerCase() ||
          v2CollectionIds.has(collectionId.toLowerCase());
        if (isHeritage) {
          const allCols = dataCollections?.collections || [];
          const heritageCols = allCols
            .filter((col) => v2CollectionIds.has(col.id.toLowerCase()))
            .map((col) => ({
              ...col,
              id: col.id.toLowerCase(),
              image: collectionImages[col.id.toLowerCase()] || "",
              count: v2ListingCounts.get(col.id.toLowerCase()) || 0,
            }));
          setSidebarCollections(
            heritageCols.length > 0 ? heritageCols : [activeCol],
          );
          setSelectedCountry({ ...DEFAULT_COUNTRY });
        } else {
          setSidebarCollections([activeCol]);
        }

        const data = await fetchListingsFromCollection(
          collectionId,
          SUBGRAPH_URL,
        );

        setListings(data);
        setCurrentPage(1);

        if (isHeritage) {
          setTraits([]);
          setSelectedTrait(null);
        } else {
          const computedTraits = computeYearSizeTraits(data);
          if (computedTraits.length > 0) {
            setTraits([{ name: "All", count: data.length }, ...computedTraits]);
            setSelectedTrait("All");
          } else {
            setTraits([]);
            setSelectedTrait(null);
          }
        }
      } catch (err) {
        console.error(err);
        setListings([]);
        setTraits([]);
      } finally {
        setLoading(false);
      }
    },
    [dataCollections, collectionImages, v2CollectionIds, v2ListingCounts],
  );

  useEffect(() => {
    const { collection, country: countryFromUrl } = searchParams;

    if (!collection || !dataCollections?.collections) return;

    const hasV2 = v2CollectionIds.size > 0;
    const prev = lastFetchRef.current;
    if (prev.collection === collection && prev.hasV2 === hasV2) return;
    lastFetchRef.current = { collection, hasV2 };

    const matchedCollection = dataCollections.collections.find(
      (col) => col.id.toLowerCase() === collection.toLowerCase(),
    );

    const country = {
      name: countryFromUrl || matchedCollection?.name || "",
      subgraphUrl: SUBGRAPH_URL,
      id: matchedCollection?.id?.toLowerCase() || collection,
    };

    setSelectedCountry(country);
    setActiveCollection(
      matchedCollection || { id: collection, name: country.name },
    );
    syncAndFetch(collection, country);
  }, [searchParams, dataCollections, syncAndFetch, v2CollectionIds]);

  const [lockScroll, setLockScroll] = useState(true);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const HERO_SCROLL_RANGE = HERO_FULL_HEIGHT - HERO_COLLAPSED_HEIGHT;

    const handleScroll = () => {
      const y = window.scrollY || 0;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;

      if (maxScroll < HERO_SCROLL_RANGE) {
        setLockScroll(false);
        return;
      }

      setScrollY(y);

      const progress = Math.min(y / HERO_SCROLL_RANGE, 1);
      setHeroShrinkProgress(progress);

      if (y >= HERO_SCROLL_RANGE) {
        setLockScroll(false);
      } else {
        setLockScroll(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (lockScroll) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [lockScroll]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const heroTranslateY =
    -(HERO_FULL_HEIGHT - HERO_COLLAPSED_HEIGHT) * heroShrinkProgress;
  useEffect(() => {
    if (isMobileMenuOpen) {
      if (closeTimeoutRef.current != null) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setShowMobileMenuBar(false);
    } else {
      closeTimeoutRef.current = setTimeout(() => {
        setShowMobileMenuBar(true);
        closeTimeoutRef.current = null;
      }, MOBILE_MENU_COLLAPSE_MS);
      return () => {
        if (closeTimeoutRef.current != null) {
          clearTimeout(closeTimeoutRef.current);
        }
      };
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const activeId = activeCollection?.id?.toLowerCase();
    const isHeritage =
      activeId === DEFAULT_COUNTRY.id.toLowerCase() ||
      v2CollectionIds.has(activeId);
    if (!activeId || !isHeritage) return;
    if (!dataCollections?.collections) return;

    const heritageCols = dataCollections.collections
      .filter((col) => v2CollectionIds.has(col.id.toLowerCase()))
      .map((col) => ({
        ...col,
        id: col.id.toLowerCase(),
        image: collectionImages[col.id.toLowerCase()] || "",
        count: v2ListingCounts.get(col.id.toLowerCase()) || 0,
      }));

    if (heritageCols.length > 0) {
      setSidebarCollections(heritageCols);
      setSelectedCountry({ ...DEFAULT_COUNTRY });
      setTraits([]);
      setSelectedTrait(null);
    }
  }, [
    v2CollectionIds,
    v2ListingCounts,
    activeCollection?.id,
    dataCollections,
    collectionImages,
  ]);

  const handleActiveCollectionChange = useCallback((collection) => {
    setActiveCollection(collection);
  }, []);

  const handleDropdownToggle = useCallback((open) => {
    setIsDropdownOpen(open);
  }, []);

  const handleTraitSelect = useCallback((traitName) => {
    setSelectedTrait((prev) => (prev === traitName ? null : traitName));
    setCurrentPage(1);
  }, []);

  const handleMobileCategorySelect = useCallback(
    (cat) => {
      const isHeritage =
        cat.id?.toLowerCase() === DEFAULT_COUNTRY.id.toLowerCase() ||
        v2CollectionIds.has(cat.id?.toLowerCase());
      const displayCountry = isHeritage
        ? DEFAULT_COUNTRY.name
        : selectedCountry.name;

      const search = buildSearch({
        country: displayCountry,
        collection: cat.id,
      });

      navigate(
        { pathname: route, search },
        {
          state: {
            country: displayCountry,
            collectionId: cat.id,
            description: cat.description,
            title: cat.name,
            image: cat.image,
          },
        },
      );

      handleActiveCollectionChange(cat);
      setIsMobileMenuOpen(false);
    },
    [
      navigate,
      route,
      selectedCountry,
      handleActiveCollectionChange,
      v2CollectionIds,
    ],
  );

  const handleCountrySelect = useCallback(
    (country) => {
      setIsDropdownOpen(false);
      setSelectedTrait(null);

      const imageUrl =
        collectionImages[country.id] ||
        country.image ||
        country.prev ||
        country.preview;

      const isHeritage =
        country.id?.toLowerCase() === DEFAULT_COUNTRY.id.toLowerCase() ||
        v2CollectionIds.has(country.id?.toLowerCase());
      const displayCountry = isHeritage ? DEFAULT_COUNTRY.name : country.name;

      setSelectedCountry(isHeritage ? { ...DEFAULT_COUNTRY } : country);

      const search = buildSearch({
        country: displayCountry,
        collection: country.id,
        image: imageUrl,
      });

      setActiveCollection({ ...country, image: imageUrl });

      navigate(
        { pathname: route, search },
        {
          state: {
            country: displayCountry,
            collectionId: country.id,
            image: imageUrl,
          },
        },
      );
    },
    [navigate, route, collectionImages, v2CollectionIds],
  );

  const filteredListings = useMemo(() => {
    if (!selectedTrait || selectedTrait === "All") return listings;

    const [type, val] = selectedTrait.split(": ");
    return listings.filter((item) => {
      if (
        item.attributes?.some(
          (a) => a.trait_type === type && String(a.value) === val,
        )
      )
        return true;
      if (type === "Year") return String(item.year) === val;
      if (type === "Size") return String(item.size) === val;
      return false;
    });
  }, [listings, selectedTrait]);

  const totalPages = Math.ceil(filteredListings.length / ITEMS_PER_PAGE);

  const paginatedListings = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredListings.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredListings, currentPage]);

  useEffect(() => {
    const HERO_SCROLL_RANGE = HERO_FULL_HEIGHT - HERO_COLLAPSED_HEIGHT;
    const id = requestAnimationFrame(() => {
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll < HERO_SCROLL_RANGE) {
        setLockScroll(false);
        setHeroShrinkProgress(1);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [paginatedListings]);

  const handleItemOpen = (item) => {
    const contractAddress =
      item.token?.collection?.id ||
      item.nft?.id ||
      item.contractAddress ||
      item.token?.collection?.id ||
      item.token?.id;

    const tokenId = item.token?.tokenId || item.tokenId || item.id;

    navigate(`/reproduction/${contractAddress}/${tokenId}`, {
      state: {
        item,
        metadataUri: item.token?.metadataUri,

        contractMetadataUri:
          item.contractMetadataUri ||
          item.token?.collection?.contractMetadataUri,

        contractURI: item.token?.collection?.contractURI,

        collectionId: item.token?.collection?.id,
      },
    });
  };
  const handleItemClose = () => setSelectedItem(null);

  const sectionOffsetTop =
    315 - (HERO_FULL_HEIGHT - HERO_COLLAPSED_HEIGHT) * heroShrinkProgress;
  const contentStyle = {
    marginTop: HERO_FULL_HEIGHT,
    transform: lockScroll ? "translateY(40px)" : "translateY(0)",
    transition: "transform 0.5s ease",
  };

  const collectionName = activeCollection?.name || selectedCountry?.name || defaultTitle;
  const canonicalUrl = `${SITE_URL}${location.pathname}${location.search}`;
  const collectionDescription = truncateDescription(
    activeCollection?.description ||
      `Browse the ${collectionName} collection on Funooni: tokenized artworks and historical archive pieces blending Arab cultural heritage with blockchain technology.`,
  );
  const collectionImage = activeCollection?.image || selectedCountry?.prev || "";
  const categorySeo = {
    title: `${collectionName} Collection | Funooni`,
    description: collectionDescription,
    canonicalUrl,
    robots: "index, follow",
    ogTitle: `${collectionName} Collection | Funooni`,
    ogDescription: collectionDescription,
    ogImage: collectionImage,
    ogUrl: canonicalUrl,
    ogType: "website",
    twitterCard: "summary_large_image",
    structuredData: buildGraph(
      organizationSchema,
      buildBreadcrumbSchema([
        { name: "Home", url: `${SITE_URL}/` },
        { name: "Reproduction", url: `${SITE_URL}/reproduction` },
        { name: collectionName, url: canonicalUrl },
      ]),
      {
        "@type": "CollectionPage",
        name: collectionName,
        description: collectionDescription,
        url: canonicalUrl,
      },
    ),
  };

  return (
    <>
      <SEO {...categorySeo} />
      <div className="country-trigger-wrapper">
        <div
          style={{
            transform: `translateY(${heroTranslateY}px)`,
            transition: "transform 0.25s ease",
          }}
          className="inner-country-trigger"
        >
          <PreHero
            image={activeCollection?.image || selectedCountry?.prev}
            className="no-margin-top"
          >
            <CountryTrigger
              selectedCountry={selectedCountry}
              groupLabel={selectedGroupLabel}
              isOpen={isDropdownOpen}
              onToggle={handleDropdownToggle}
            />
          </PreHero>

          <CountryDropdownPanel
            selectedCountry={selectedCountry}
            onSelect={handleCountrySelect}
            isOpen={isDropdownOpen}
            onToggle={handleDropdownToggle}
            groups={countryGroups}
          />
        </div>
      </div>

      {!loading && (
        <div className="tabletSidebar">
          <div className="tabletSidebar-description-wrapper">
            {tabletDescriptionState && (
              <SidebarDescription
                activeCollection={tabletDescriptionState.activeCollection}
                isDescriptionExpanded={
                  tabletDescriptionState.isDescriptionExpanded
                }
                onToggleExpanded={
                  tabletDescriptionState.setIsDescriptionExpanded
                }
                fullDescription={tabletDescriptionState.fullDescription}
                collapsedDescription={
                  tabletDescriptionState.collapsedDescription
                }
                shouldTruncate={tabletDescriptionState.shouldTruncate}
              />
            )}
          </div>

          <nav className="mobile-menu" aria-label="Categories or traits">
            {showMobileMenuBar && (
              <button
                type="button"
                className="mobile-menu-bar mobile-menu-bar--visible"
                onClick={() => {
                  if (closeTimeoutRef.current != null) {
                    clearTimeout(closeTimeoutRef.current);
                    closeTimeoutRef.current = null;
                  }
                  setShowMobileMenuBar(false);
                  setIsMobileMenuOpen(true);
                }}
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle menu"
              >
                <BurgerIcon />
              </button>
            )}

            <div
              className={`mobile-menu-panel ${
                isMobileMenuOpen ? "mobile-menu-panel--open" : ""
              }`}
            >
              <h3 className="mobile-menu-title">
                {traits && traits.length > 0 ? "Traits" : "Categories"}
              </h3>

              <ul className="mobile-menu-list">
                {traits && traits.length > 0
                  ? traits.map((trait) => (
                      <li
                        key={trait.name}
                        className={`mobile-menu-item ${
                          selectedTrait === trait.name
                            ? "mobile-menu-item--active"
                            : ""
                        }`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          handleTraitSelect(trait.name);
                          setIsMobileMenuOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleTraitSelect(trait.name);
                            setIsMobileMenuOpen(false);
                          }
                        }}
                      >
                        {trait.name} ({trait.count})
                      </li>
                    ))
                  : sidebarCollections.map((cat) => (
                      <li
                        key={cat.id || cat.name}
                        className={`mobile-menu-item ${
                          activeCollection?.id === cat.id
                            ? "mobile-menu-item--active"
                            : ""
                        }`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleMobileCategorySelect(cat)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleMobileCategorySelect(cat);
                          }
                        }}
                      >
                        {cat.name} ({cat.count ?? 0})
                      </li>
                    ))}
              </ul>

              <div
                className="mobile-menu-handle"
                aria-hidden
                onClick={() => setIsMobileMenuOpen((o) => !o)}
              />
            </div>
          </nav>
        </div>
      )}

      <div className="Category-page">
        <Sidebar
          heroCollapsed={heroShrinkProgress === 1}
          categories={sidebarCollections}
          activeCollection={activeCollection}
          route={route}
          title={activeCollection?.name || defaultTitle}
          subtitle={traits && traits.length > 0 ? "Traits" : "Categories"}
          traits={traits}
          selectedTrait={selectedTrait}
          countryName={selectedCountry.name}
          onTraitSelect={setSelectedTrait}
          onDescriptionStateChange={setTabletDescriptionState}
          style={{
            marginTop: `${sectionOffsetTop}px`,
            transition: "margin-top 0.25s ease, min-height 0.25s ease",
          }}
        />
        <div
          className={`Category-content${
            heroShrinkProgress === 1 ? " hero-collapsed" : ""
          }`}
          style={contentStyle}
        >
          {loading ? (
            <p>Loading...</p>
          ) : filteredListings.length > 0 ? (
            paginatedListings.map((item) => (
              <PreCard
                key={item.id}
                item={item}
                onClick={() => handleItemOpen(item)}
                marketAddress={MARKET_ADDRESS}
                marketAbi={MARKET_ABI}
                tokens="tokens"
                ETH="ETH"
                useFormattedPrice={false}
              />
            ))
          ) : (
            <p>No items found in this collection</p>
          )}
        </div>
        <Modal
          ETH="ETH"
          tokens="tokens"
          item={selectedItem}
          onClose={handleItemClose}
          MARKET_ADDRESS={MARKET_ADDRESS}
          MARKET_ABI={MARKET_ABI}
          useFormattedPrice={false}
        />{" "}
      </div>

      {totalPages > 1 && (
        <div className="Category-pagination">
          <button
            className="pagination-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ‹ Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) =>
                p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1,
            )
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1)
                acc.push({ type: "ellipsis", key: `e${idx}` });
              acc.push({ type: "page", value: p });
              return acc;
            }, [])
            .map((item) =>
              item.type === "ellipsis" ? (
                <span
                  key={item.key}
                  className="pagination-page"
                  style={{ border: "none", cursor: "default" }}
                >
                  …
                </span>
              ) : (
                <button
                  key={item.value}
                  className={`pagination-page${currentPage === item.value ? " active" : ""}`}
                  onClick={() => setCurrentPage(item.value)}
                >
                  {item.value}
                </button>
              ),
            )}

          <button
            className="pagination-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            Next ›
          </button>
        </div>
      )}
    </>
  );
};

export default Category;
